import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

// Types
import { ITitlingSubscriptionResponse } from "@DataPages/types/titling/model";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";

// Hooks
import { getTitlingsQueryKey } from "./useTitlingQuery";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Subscriptions
import { onUpdateTitlings } from "@DataPages/api/financeManagement/titling/subscriptions";

export function useTitlingUpdateSubscription(args: { locationId: string; commissionCycleId: string; payPeriodId: string }) {
	const { locationId, commissionCycleId, payPeriodId } = args;
	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();
	const queryClient = useQueryClient();

	const subscriptionKey = ["titlingSub", locationId, commissionCycleId, payPeriodId] as const;

	useEffect(() => {
		const shouldSubscribe = locationId && commissionCycleId && payPeriodId;

		if (shouldSubscribe) {
			addSubscription(subscriptionKey, [
				{
					context: "onUpdateTitlings",
					query: onUpdateTitlings,
					variables: { commissionCycleId, payPeriodId },
				},
			]);
		}

		return () => {
			if (shouldSubscribe) {
				deleteSubscription(subscriptionKey);
			}
		};
	}, [locationId, commissionCycleId, payPeriodId, addSubscription, deleteSubscription]);

	const handleUpdateTitling = (result: GraphqlSubscriptionResponse<"onUpdateTitlings", ITitlingSubscriptionResponse>): void => {
		const titlings = result.data?.onUpdateTitlings?.titlings;
		if (!Array.isArray(titlings)) {
			return;
		}

		const queryKey = getTitlingsQueryKey({ locationId, commissionCycleId, payPeriodId });
		queryClient.resetQueries({ queryKey });
	};

	const titlingSubscription = getSubscription(subscriptionKey);
	titlingSubscription?.subscriptions?.onUpdateTitlings?.setCallback(handleUpdateTitling);
}
